import React, { useMemo, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { useAuth } from "hooks/useAuth";
import { useUser } from "contexts/v1/masterData/UserContext";
import { deleteRequest, getRequest, postRequest } from "services/api";
import { formatDateToLocal } from "utils/utils";

const DEV_ROLE_NAMES = ["dev", "developer", "superadmin"];

const selectStyles = {
  menu: (base) => ({ ...base, zIndex: 1060 }),
  menuPortal: (base) => ({ ...base, zIndex: 1060 }),
};

const toDateKey = (date) => {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
  return formatDateToLocal(date instanceof Date ? date : new Date(date));
};

const addDays = (date, days) => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + days);
  return nextDate;
};

const getDatesBetween = (startDate, endDate) => {
  const dates = [];
  const start = new Date(`${startDate}T00:00:00`);
  const end = new Date(`${endDate}T00:00:00`);
  for (let current = start; current <= end; current = addDays(current, 1)) {
    dates.push(toDateKey(current));
  }
  return dates;
};

const randomItem = (items) => items[Math.floor(Math.random() * items.length)];
const randomMinute = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

const shiftTime = (time, minuteOffset) => {
  const [hour, minute] = String(time || "08:00").split(":").map(Number);
  const totalMinutes = hour * 60 + minute + minuteOffset;
  const normalized = ((totalMinutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(normalized / 60)).padStart(2, "0")}:${String(normalized % 60).padStart(2, "0")}`;
};

const BulkAttendanceDevTool = ({ startDate, endDate, attendanceData = [], onSuccess }) => {
  const { user } = useAuth();
  const { users } = useUser();
  const [show, setShow] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    start_date: toDateKey(startDate) || toDateKey(new Date()),
    end_date: toDateKey(endDate) || toDateKey(new Date()),
    action_mode: "create",
    user_mode: "random",
    selected_user_ids: [],
    random_user_count: 10,
    day_mode: "all",
    check_in: "08:00",
    check_out: "17:00",
    randomize_time: true,
    skip_weekend: false,
  });

  const canUseDevTool = useMemo(() => {
    const roles = user?.roles?.map((role) => String(role?.name || role).toLowerCase()) || [];
    return roles.some((role) => DEV_ROLE_NAMES.includes(role));
  }, [user]);

  const userOptions = useMemo(
    () => (users.data || []).map((item) => ({
      value: item.id,
      label: `${item.name}${item.department?.name ? ` - ${item.department.name}` : ""}`,
    })),
    [users.data]
  );

  if (!canUseDevTool) return null;

  const updateForm = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const getTargetUsers = () => {
    if (form.action_mode === "delete" && form.user_mode === "random") {
      return userOptions.map((option) => option.value);
    }

    if (form.user_mode === "selected") {
      return form.selected_user_ids;
    }

    const shuffled = [...userOptions].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.max(1, Number(form.random_user_count || 1))).map((option) => option.value);
  };

  const getTargetDates = () => {
    const dates = getDatesBetween(form.start_date, form.end_date).filter((date) => {
      if (!form.skip_weekend) return true;
      const day = new Date(`${date}T00:00:00`).getDay();
      return day !== 0 && day !== 6;
    });

    if (form.day_mode === "random") {
      return dates.filter(() => Math.random() > 0.3);
    }

    return dates;
  };

  const buildPayload = (userId, date) => {
    const checkIn = form.randomize_time
      ? shiftTime(form.check_in, randomMinute(-15, 20))
      : form.check_in;
    const checkOut = form.randomize_time
      ? shiftTime(form.check_out, randomMinute(-10, 45))
      : form.check_out;

    return {
      user_id: userId,
      date,
      check_in: checkIn,
      check_out: checkOut,
    };
  };

  const getAttendanceDateKey = (record = {}) => {
    const source = record.check_in || record.date || record.attendance_date;
    if (!source) return "";
    return toDateKey(new Date(source));
  };

  const getAttendanceUserId = (record = {}) => record.user_id || record.user?.id;

  const fetchAttendanceForDelete = async () => {
    const params = new URLSearchParams({
      all: "1",
      include: "user",
      "filter[start_date]": form.start_date,
      "filter[end_date]": form.end_date,
    });

    const response = await getRequest(`/ahr/absensis?${params.toString()}`);
    return response?.data || [];
  };

  const handleDeleteBulk = async (targetUsers, targetDates) => {
    const targetUserSet = new Set(targetUsers.map(String));
    const targetDateSet = new Set(targetDates);
    const latestAttendanceData = await fetchAttendanceForDelete().catch(() => attendanceData || []);
    const matchedRecords = (latestAttendanceData || []).filter((record) => (
      targetUserSet.has(String(getAttendanceUserId(record))) &&
      targetDateSet.has(getAttendanceDateKey(record))
    ));

    if (!matchedRecords.length) {
      Swal.fire("Data kosong", "Tidak ada data absensi yang cocok untuk dihapus pada filter ini.", "info");
      return;
    }

    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Hapus data absensi?",
      text: `${matchedRecords.length} data absensi pada tanggal terpilih akan dihapus dan menjadi kosong.`,
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Ya, hapus",
      cancelButtonText: "Batal",
    });

    if (!confirmation.isConfirmed) return;

    setIsSubmitting(true);
    const failedItems = [];

    try {
      for (const record of matchedRecords) {
        try {
          await deleteRequest(`/ahr/absensis/${record.id}`);
        } catch (error) {
          failedItems.push({ id: record.id, message: error.message });
        }
      }

      if (failedItems.length) {
        const sampleErrors = failedItems
          .slice(0, 5)
          .map((item) => `${item.id}: ${item.message}`)
          .join("\n");
        Swal.fire("Sebagian gagal", `${matchedRecords.length - failedItems.length} berhasil dihapus, ${failedItems.length} gagal.\n${sampleErrors}`, "warning");
      } else {
        Swal.fire("Berhasil", `${matchedRecords.length} data absensi berhasil dihapus.`, "success");
        setShow(false);
      }

      onSuccess?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const targetUsers = getTargetUsers();
    const targetDates = getTargetDates();
    const totalRows = targetUsers.length * targetDates.length;

    if (!targetUsers.length) {
      Swal.fire("User kosong", "Pilih minimal 1 staff atau gunakan mode random.", "warning");
      return;
    }

    if (!targetDates.length) {
      Swal.fire("Tanggal kosong", "Rentang tanggal tidak menghasilkan data.", "warning");
      return;
    }

    if (form.action_mode === "delete") {
      await handleDeleteBulk(targetUsers, targetDates);
      return;
    }

    if (totalRows > 500) {
      Swal.fire("Terlalu banyak data", "Maksimal 500 absensi per proses agar server tetap aman.", "warning");
      return;
    }

    const confirmation = await Swal.fire({
      icon: "question",
      title: "Create bulk attendance?",
      text: `${totalRows} data absensi dummy akan dibuat untuk kebutuhan dev.`,
      showCancelButton: true,
      confirmButtonText: "Ya, buat data",
      cancelButtonText: "Batal",
    });

    if (!confirmation.isConfirmed) return;

    setIsSubmitting(true);
    const failedItems = [];

    try {
      for (const targetDate of targetDates) {
        for (const userId of targetUsers) {
          try {
            await postRequest("/ahr/absensis", buildPayload(userId, targetDate));
          } catch (error) {
            failedItems.push({ userId, date: targetDate, message: error.message });
          }
        }
      }

      if (failedItems.length) {
        const sampleErrors = failedItems
          .slice(0, 5)
          .map((item) => `${item.date}: ${item.message}`)
          .join("\n");
        Swal.fire("Sebagian gagal", `${totalRows - failedItems.length} berhasil, ${failedItems.length} gagal.\n${sampleErrors}`, "warning");
      } else {
        Swal.fire("Berhasil", `${totalRows} data absensi dummy berhasil dibuat.`, "success");
        setShow(false);
      }

      onSuccess?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedUserOptions = userOptions.filter((option) => form.selected_user_ids.includes(option.value));

  return (
    <>
      <Button
        variant="outline-dark"
        size="sm"
        className="fw-bold shadow-sm px-3"
        onClick={() => setShow(true)}
        title="Khusus role dev untuk membuat data absensi dummy"
      >
        Bulk Dev Attendance
      </Button>

      <Modal show={show} onHide={() => !isSubmitting && setShow(false)} centered size="lg" backdrop="static">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton={!isSubmitting}>
            <Modal.Title>Bulk Attendance Dev</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="alert alert-warning py-2">
              Tool khusus dev. Mode create mengisi absensi dummy, mode delete mengosongkan absensi pada tanggal/range yang dipilih untuk testing seperti Over DO.
            </div>

            <div className="row g-3">
              <div className="col-md-4">
                <Form.Label>Mode</Form.Label>
                <Form.Select value={form.action_mode} onChange={(event) => updateForm("action_mode", event.target.value)} disabled={isSubmitting}>
                  <option value="create">Create / Isi Absensi</option>
                  <option value="delete">Delete / Kosongkan Absensi</option>
                </Form.Select>
              </div>
              <div className="col-md-4">
                <Form.Label>Start Date</Form.Label>
                <Form.Control type="date" value={form.start_date} onChange={(event) => updateForm("start_date", event.target.value)} disabled={isSubmitting} required />
              </div>
              <div className="col-md-4">
                <Form.Label>End Date</Form.Label>
                <Form.Control type="date" value={form.end_date} onChange={(event) => updateForm("end_date", event.target.value)} disabled={isSubmitting} required />
              </div>
              <div className="col-md-4">
                <Form.Label>Mode Hari</Form.Label>
                <Form.Select value={form.day_mode} onChange={(event) => updateForm("day_mode", event.target.value)} disabled={isSubmitting}>
                  <option value="all">Semua tanggal</option>
                  <option value="random">Random sebagian tanggal</option>
                </Form.Select>
              </div>

              <div className="col-md-4">
                <Form.Label>Mode Staff</Form.Label>
                <Form.Select value={form.user_mode} onChange={(event) => updateForm("user_mode", event.target.value)} disabled={isSubmitting}>
                  <option value="random">{form.action_mode === "delete" ? "Semua staff di range" : "Random staff"}</option>
                  <option value="selected">Pilih staff</option>
                </Form.Select>
              </div>
              <div className="col-md-8">
                {form.user_mode === "selected" ? (
                  <>
                    <Form.Label>Pilih Staff</Form.Label>
                    <Select
                      isMulti
                      options={userOptions}
                      value={selectedUserOptions}
                      onChange={(options) => updateForm("selected_user_ids", (options || []).map((option) => option.value))}
                      isDisabled={isSubmitting || users.loading}
                      isLoading={users.loading}
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      styles={selectStyles}
                      placeholder="Pilih staff..."
                    />
                  </>
                ) : (
                  <>
                    <Form.Label>Jumlah Random Staff</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max={Math.max(1, userOptions.length)}
                      value={form.random_user_count}
                      onChange={(event) => updateForm("random_user_count", event.target.value)}
                      disabled={isSubmitting}
                    />
                  </>
                )}
              </div>

              {form.action_mode === "create" && <div className="col-md-3">
                <Form.Label>Check In</Form.Label>
                <Form.Control type="time" value={form.check_in} onChange={(event) => updateForm("check_in", event.target.value)} disabled={isSubmitting} required />
              </div>}
              {form.action_mode === "create" && <div className="col-md-3">
                <Form.Label>Check Out</Form.Label>
                <Form.Control type="time" value={form.check_out} onChange={(event) => updateForm("check_out", event.target.value)} disabled={isSubmitting} required />
              </div>}
              {form.action_mode === "create" && <div className="col-md-3 d-flex align-items-end">
                <label className="form-check mb-2">
                  <input className="form-check-input" type="checkbox" checked={form.randomize_time} onChange={(event) => updateForm("randomize_time", event.target.checked)} disabled={isSubmitting} />
                  <span className="form-check-label">Random menit</span>
                </label>
              </div>}
              <div className="col-md-3 d-flex align-items-end">
                <label className="form-check mb-2">
                  <input className="form-check-input" type="checkbox" checked={form.skip_weekend} onChange={(event) => updateForm("skip_weekend", event.target.checked)} disabled={isSubmitting} />
                  <span className="form-check-label">Skip Sabtu/Minggu</span>
                </label>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShow(false)} disabled={isSubmitting}>Batal</Button>
            <Button variant={form.action_mode === "delete" ? "danger" : "dark"} type="submit" disabled={isSubmitting || users.loading}>
              {isSubmitting ? (
                <><Spinner animation="border" size="sm" className="me-2" /> Memproses...</>
              ) : form.action_mode === "delete" ? (
                "Delete / Kosongkan Absensi"
              ) : (
                "Create Bulk Attendance"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default BulkAttendanceDevTool;
